import MERCHANT_TIER from '@/constants/merchant-tier'
import MERCHANT_STATUS from '@/constants/merchant-status'

/**
 * Checks if a merchant can access the API view based on their tier.
 *
 * @param {string} merchantTier - The tier of the merchant.
 * @return {boolean} True if the merchant can access the API view, false otherwise.
 */
const canUserAccessApiViewByMerchantTier = (merchantTier) => {
  const ACCEPTABLE_MERCHANT_TIER = [
    MERCHANT_TIER.tier_2,
    MERCHANT_TIER.tier_3,
    MERCHANT_TIER.t3,
    MERCHANT_TIER.tier_3_unverified,
  ]

  return ACCEPTABLE_MERCHANT_TIER.includes(merchantTier)
}

/**
 * Checks if a merchant can access the API view based on their type.
 *
 * @param {object} options - An object containing the merchant's online and in-store status.
 * @param {boolean} options.isMerchantOnline - Whether the merchant is online.
 * @param {boolean} options.isMerchantInStore - Whether the merchant is in-store.
 * @param {object} merchantProfile - The merchant's profile.
 * @return {boolean} True if the merchant can access the API view, false otherwise.
 */
const canUserAccessApiViewByMerchantType = (
  { isMerchantOnline, isMerchantInStore },
  merchantProfile
) => {
  if (isMerchantOnline) {
    return true
  }

  return isMerchantInStore && merchantProfile?.inStoreDirectIntegrationEnabled
}

/**
 * Checks if a merchant can access the API view based on their status.
 *
 * @param {string} merchantStatus - The status of the merchant.
 * @return {boolean} True if the merchant can access the API view, false otherwise.
 */
const canUserAccessApiViewByMerchantStatus = (merchantStatus) => {
  const ACCEPTABLE_MERCHANT_STATUS = [
    MERCHANT_STATUS.signed,
    MERCHANT_STATUS.live,
  ]

  return ACCEPTABLE_MERCHANT_STATUS.includes(merchantStatus)
}

/**
 * An old version of the canUserAccessApiViewByMerchantStatus method. It will be removed in the future.
 * Right now, this function is used for SidebarItem only
 * Checks if a merchant can access the API view based on their status.
 *
 * @param {string} merchantStatus - The status of the merchant.
 * @param {string} merchantTier - The tier of the merchant.
 * @return {boolean} True if the merchant can access the API view, false otherwise.
 */
const canUserAccessApiViewByMerchantStatusLegacy = (
  merchantStatus,
  merchantTier
) => {
  if (
    merchantStatus === MERCHANT_STATUS.signed &&
    merchantTier === MERCHANT_TIER.tier_3_unverified
  ) {
    return true
  }

  const ACCEPTABLE_MERCHANT_STATUS = [
    MERCHANT_STATUS.live,
    MERCHANT_STATUS.active,
    MERCHANT_STATUS.inactive,
    MERCHANT_STATUS.churned,
    MERCHANT_STATUS.lost,
    MERCHANT_STATUS.test,
  ]

  return ACCEPTABLE_MERCHANT_STATUS.includes(merchantStatus)
}

export {
  canUserAccessApiViewByMerchantTier,
  canUserAccessApiViewByMerchantType,
  canUserAccessApiViewByMerchantStatus,
  canUserAccessApiViewByMerchantStatusLegacy,
}
